import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, Col, Form, Row, Table } from 'react-bootstrap';
import { OpenAPI } from '../api/core/OpenAPI';
import { request as __request } from '../api/core/request';
import '../../sass/pages/deployments.scss';

type DeploymentView = 'view' | 'modify' | 'manage' | 'history';
type AlertType = 'success' | 'danger' | 'info' | 'warning';

interface Feature {
  id: number;
  name: string;
  createdAt?: string;
}

interface DeploymentFeature {
  featureId: number;
  featureName: string;
  isInstalled: boolean;
}

interface Deployment {
  id?: number;
  name: string;
  release: string;
  additionalNotes: string;
  features: DeploymentFeature[];
  createdAt?: string;
  updatedAt?: string;
}

interface DeploymentHistoryItem {
  id?: number;
  deploymentId?: number;
  date: string;
  details: string;
  createdAt?: string;
  updatedAt?: string;
}

const emptyDeployment: Deployment = {
  name: '',
  release: '',
  additionalNotes: '',
  features: [],
};

const emptyHistoryItem: DeploymentHistoryItem = {
  date: '',
  details: '',
};

const HistoryIcon: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
    <path d="M8 3a5 5 0 1 0 4.546 2.914.5.5 0 0 1 .908-.417A6 6 0 1 1 8 2v1z" />
    <path d="M8 4.466V8l3 1.5-.5.866L7 8.5V4.466h1z" />
    <path d="M8 0a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-1 0v-1A.5.5 0 0 1 8 0z" />
  </svg>
);

const Deployments: React.FC = () => {
  const [activeView, setActiveView] = useState<DeploymentView>('view');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [deployments, setDeployments] = useState<Deployment[]>([]);
  const [features, setFeatures] = useState<Feature[]>([]);
  const [selectedDeploymentId, setSelectedDeploymentId] = useState<number | null>(null);
  const [formData, setFormData] = useState<Deployment>(emptyDeployment);
  const [featureInstalls, setFeatureInstalls] = useState<Record<number, boolean>>({});
  const [newFeaturesInput, setNewFeaturesInput] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [statusType, setStatusType] = useState<AlertType>('info');
  const [loading, setLoading] = useState(false);
  const [historyDeployment, setHistoryDeployment] = useState<Deployment | null>(null);
  const [historyItems, setHistoryItems] = useState<DeploymentHistoryItem[]>([]);
  const [selectedHistoryItemId, setSelectedHistoryItemId] = useState<number | null>(null);
  const [historyFormData, setHistoryFormData] = useState<DeploymentHistoryItem>(emptyHistoryItem);

  useEffect(() => {
    loadDeployments();
    loadFeatures();
  }, []);

  const loadDeployments = async () => {
    try {
      const response = await __request(OpenAPI, {
        method: 'GET',
        url: '/api/apitest/deployments/',
      }) as Deployment[];
      setDeployments(response);
    } catch (error) {
      setStatusMessage(`Error loading deployments: ${String(error)}`);
      setStatusType('danger');
    }
  };

  const loadFeatures = async () => {
    try {
      const response = await __request(OpenAPI, {
        method: 'GET',
        url: '/api/apitest/features/',
      }) as Feature[];
      setFeatures(response);
    } catch (error) {
      setStatusMessage(`Error loading features: ${String(error)}`);
      setStatusType('danger');
    }
  };

  const selectDeploymentForEdit = (deployment: Deployment) => {
    setSelectedDeploymentId(deployment.id ?? null);
    setFormData({ ...emptyDeployment, ...deployment });
    const installMap: Record<number, boolean> = {};
    deployment.features.forEach(f => { installMap[f.featureId] = f.isInstalled; });
    setFeatureInstalls(installMap);
    setActiveView('modify');
  };

  const resetForm = () => {
    setSelectedDeploymentId(null);
    setFormData(emptyDeployment);
    setFeatureInstalls({});
  };

  const saveDeployment = async () => {
    if (!formData.name.trim()) {
      setStatusMessage('Deployment name is required.');
      setStatusType('danger');
      return;
    }

    setLoading(true);
    try {
      const body = {
        name: formData.name,
        release: formData.release,
        additionalNotes: formData.additionalNotes,
      };

      let saved: Deployment;
      if (selectedDeploymentId) {
        saved = await __request(OpenAPI, {
          method: 'PATCH',
          url: `/api/apitest/deployments/${selectedDeploymentId}/`,
          body,
        }) as Deployment;
      } else {
        saved = await __request(OpenAPI, {
          method: 'POST',
          url: '/api/apitest/deployments/',
          body,
        }) as Deployment;
        setSelectedDeploymentId(saved.id ?? null);
      }

      const featuresPayload = features.map(f => ({
        featureId: f.id,
        isInstalled: featureInstalls[f.id] ?? false,
      }));

      const updated = await __request(OpenAPI, {
        method: 'PATCH',
        url: `/api/apitest/deployments/${saved.id}/features/`,
        body: { features: featuresPayload },
      }) as Deployment;

      setFormData({ ...emptyDeployment, ...updated });
      const installMap: Record<number, boolean> = {};
      updated.features.forEach(f => { installMap[f.featureId] = f.isInstalled; });
      setFeatureInstalls(installMap);

      setStatusMessage(`Deployment ${selectedDeploymentId ? 'updated' : 'created'} successfully.`);
      setStatusType('success');
      await loadDeployments();
    } catch (error) {
      setStatusMessage(`Error saving deployment: ${String(error)}`);
      setStatusType('danger');
    } finally {
      setLoading(false);
    }
  };

  const deleteDeployment = async (deployment: Deployment) => {
    if (!deployment.id) return;
    setLoading(true);
    try {
      await __request(OpenAPI, {
        method: 'DELETE',
        url: `/api/apitest/deployments/${deployment.id}/`,
      });
      setStatusMessage('Deployment deleted successfully.');
      setStatusType('success');
      if (selectedDeploymentId === deployment.id) resetForm();
      await loadDeployments();
    } catch (error) {
      setStatusMessage(`Error deleting deployment: ${String(error)}`);
      setStatusType('danger');
    } finally {
      setLoading(false);
    }
  };

  const addFeatures = async () => {
    if (!newFeaturesInput.trim()) {
      setStatusMessage('Please enter at least one feature name.');
      setStatusType('warning');
      return;
    }
    setLoading(true);
    try {
      await __request(OpenAPI, {
        method: 'POST',
        url: '/api/apitest/features/',
        body: { names: newFeaturesInput },
      });
      setNewFeaturesInput('');
      setStatusMessage('Features added successfully.');
      setStatusType('success');
      await loadFeatures();
      await loadDeployments();
    } catch (error) {
      setStatusMessage(`Error adding features: ${String(error)}`);
      setStatusType('danger');
    } finally {
      setLoading(false);
    }
  };

  const deleteFeature = async (feature: Feature) => {
    setLoading(true);
    try {
      await __request(OpenAPI, {
        method: 'DELETE',
        url: `/api/apitest/features/${feature.id}/`,
      });
      setStatusMessage('Feature deleted successfully.');
      setStatusType('success');
      await loadFeatures();
      await loadDeployments();
    } catch (error) {
      setStatusMessage(`Error deleting feature: ${String(error)}`);
      setStatusType('danger');
    } finally {
      setLoading(false);
    }
  };

  const loadHistory = async (deploymentId: number) => {
    try {
      const response = await __request(OpenAPI, {
        method: 'GET',
        url: `/api/apitest/deployments/${deploymentId}/history/`,
      }) as DeploymentHistoryItem[];
      setHistoryItems(response);
    } catch (error) {
      setStatusMessage(`Error loading deployment history: ${String(error)}`);
      setStatusType('danger');
    }
  };

  const openHistory = (deployment: Deployment) => {
    if (!deployment.id) return;
    setHistoryDeployment(deployment);
    setSelectedHistoryItemId(null);
    setHistoryFormData(emptyHistoryItem);
    loadHistory(deployment.id);
  };

  const closeHistory = () => {
    setHistoryDeployment(null);
    setHistoryItems([]);
    setSelectedHistoryItemId(null);
    setHistoryFormData(emptyHistoryItem);
  };

  const openHistoryPage = (deployment: Deployment) => {
    openHistory(deployment);
    setActiveView('history');
  };

  const closeHistoryPage = () => {
    closeHistory();
    setActiveView('modify');
  };

  const selectHistoryItemForEdit = (item: DeploymentHistoryItem) => {
    setSelectedHistoryItemId(item.id ?? null);
    setHistoryFormData({ ...emptyHistoryItem, ...item });
  };

  const resetHistoryForm = () => {
    setSelectedHistoryItemId(null);
    setHistoryFormData(emptyHistoryItem);
  };

  const saveHistoryItem = async () => {
    if (!historyDeployment?.id) return;
    if (!historyFormData.date) {
      setStatusMessage('Date is required.');
      setStatusType('danger');
      return;
    }

    setLoading(true);
    try {
      const body = {
        date: historyFormData.date,
        details: historyFormData.details,
      };

      if (selectedHistoryItemId) {
        await __request(OpenAPI, {
          method: 'PATCH',
          url: `/api/apitest/deployments/${historyDeployment.id}/history/${selectedHistoryItemId}/`,
          body,
        });
      } else {
        await __request(OpenAPI, {
          method: 'POST',
          url: `/api/apitest/deployments/${historyDeployment.id}/history/`,
          body,
        });
      }

      resetHistoryForm();
      setStatusMessage(`History item ${selectedHistoryItemId ? 'updated' : 'added'} successfully.`);
      setStatusType('success');
      await loadHistory(historyDeployment.id);
    } catch (error) {
      setStatusMessage(`Error saving history item: ${String(error)}`);
      setStatusType('danger');
    } finally {
      setLoading(false);
    }
  };

  const deleteHistoryItem = async (item: DeploymentHistoryItem) => {
    if (!historyDeployment?.id || !item.id) return;
    setLoading(true);
    try {
      await __request(OpenAPI, {
        method: 'DELETE',
        url: `/api/apitest/deployments/${historyDeployment.id}/history/${item.id}/`,
      });
      setStatusMessage('History item deleted successfully.');
      setStatusType('success');
      if (selectedHistoryItemId === item.id) resetHistoryForm();
      await loadHistory(historyDeployment.id);
    } catch (error) {
      setStatusMessage(`Error deleting history item: ${String(error)}`);
      setStatusType('danger');
    } finally {
      setLoading(false);
    }
  };

  const renderSidebar = () => (
    <div className={`sidebar left ${sidebarOpen ? 'open' : 'closed'}`}>
      <Button variant="outline-primary" size="sm" onClick={() => setSidebarOpen(v => !v)} className="toggle-btn">
        {sidebarOpen ? '<' : '>'}
      </Button>
      {sidebarOpen && (
        <div className="deployments-sidebar-actions">
          <h4>Deployments</h4>
          <div className="d-grid gap-2">
            <Button
              variant={activeView === 'view' ? 'primary' : 'outline-primary'}
              onClick={() => setActiveView('view')}
            >
              View Deployments
            </Button>
            <Button
              variant={activeView === 'modify' ? 'primary' : 'outline-primary'}
              onClick={() => { resetForm(); setActiveView('modify'); }}
            >
              Modify Deployment
            </Button>
            <Button
              variant={activeView === 'manage' ? 'primary' : 'outline-primary'}
              onClick={() => setActiveView('manage')}
            >
              Manage Features
            </Button>
          </div>
        </div>
      )}
    </div>
  );

  const renderViewDeployments = () => (
    <>
      <h4>View Deployments</h4>
      {deployments.length === 0 ? (
        <Card>
          <Card.Body>
            <p className="text-muted">No deployments found. Use "Modify Deployment" to create one.</p>
          </Card.Body>
        </Card>
      ) : (
        <div className="deployment-table-wrapper">
          <Table bordered responsive className="deployment-matrix-table">
            <thead>
              <tr>
                <th>Deployment</th>
                <th>Release</th>
                <th>History</th>
                {features.map(f => <th key={f.id}>{f.name}</th>)}
                <th>Additional Notes</th>
              </tr>
            </thead>
            <tbody>
              {deployments.map(dep => (
                <tr key={dep.id} className={historyDeployment?.id === dep.id ? 'table-active' : ''}>
                  <td><strong>{dep.name}</strong></td>
                  <td>{dep.release}</td>
                  <td>
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      title="Show deployment history"
                      onClick={() => openHistory(dep)}
                    >
                      <HistoryIcon />
                    </Button>
                  </td>
                  {features.map(f => {
                    const df = dep.features.find(item => item.featureId === f.id);
                    const installed = df?.isInstalled ?? false;
                    return (
                      <td key={f.id} className={installed ? 'feature-installed' : 'feature-not-installed'}>
                        {installed ? 'Installed' : 'Not Installed'}
                      </td>
                    );
                  })}
                  <td className="notes-cell">{dep.additionalNotes}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}

      {historyDeployment && renderHistorySection()}
    </>
  );

  const renderModifyDeployment = () => (
    <>
      <h4>{selectedDeploymentId ? 'Edit Deployment' : 'New Deployment'}</h4>
      <Card>
        <Card.Body>
          <Row>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label><strong>Name</strong></Form.Label>
                <Form.Control
                  value={formData.name}
                  onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label><strong>Release</strong></Form.Label>
                <Form.Control
                  value={formData.release}
                  onChange={e => setFormData(prev => ({ ...prev, release: e.target.value }))}
                />
              </Form.Group>
            </Col>
          </Row>

          {features.length > 0 && (
            <Form.Group className="mb-3">
              <Form.Label><strong>Features</strong></Form.Label>
              <div className="feature-checklist">
                {features.map(f => (
                  <Form.Check
                    key={f.id}
                    id={`feature-${f.id}`}
                    checked={featureInstalls[f.id] ?? false}
                    label={f.name}
                    onChange={e => setFeatureInstalls(prev => ({ ...prev, [f.id]: e.target.checked }))}
                  />
                ))}
              </div>
            </Form.Group>
          )}

          {features.length === 0 && (
            <p className="text-muted mb-3">No features defined yet. Add features in the "Manage Features" section.</p>
          )}

          <Form.Group className="mb-3">
            <Form.Label><strong>Additional Notes</strong></Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={formData.additionalNotes}
              onChange={e => setFormData(prev => ({ ...prev, additionalNotes: e.target.value }))}
            />
          </Form.Group>

          <div className="d-flex gap-2">
            <Button onClick={saveDeployment} disabled={loading}>
              {selectedDeploymentId ? 'Update Deployment' : 'Create Deployment'}
            </Button>
            <Button variant="secondary" onClick={resetForm} disabled={loading}>New</Button>
          </div>
        </Card.Body>
      </Card>

      {deployments.length > 0 && (
        <>
          <h5 className="mt-3">Existing Deployments</h5>
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th>Name</th>
                <th>Release</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {deployments.map(dep => (
                <tr key={dep.id} className={selectedDeploymentId === dep.id ? 'table-active' : ''}>
                  <td>{dep.name}</td>
                  <td>{dep.release}</td>
                  <td>
                    <div className="d-flex gap-2">
                      <Button size="sm" variant="outline-primary" onClick={() => selectDeploymentForEdit(dep)}>Edit</Button>
                      <Button size="sm" variant="outline-secondary" onClick={() => openHistoryPage(dep)}>Edit History</Button>
                      <Button size="sm" variant="outline-danger" onClick={() => deleteDeployment(dep)} disabled={loading}>Delete</Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </>
      )}
    </>
  );

  const renderManageFeatures = () => (
    <>
      <h4>Manage Features</h4>
      <Card>
        <Card.Header><h5>Add Features</h5></Card.Header>
        <Card.Body>
          <Form.Group className="mb-3">
            <Form.Label>Feature Names <span className="text-muted">(comma-separated)</span></Form.Label>
            <Form.Control
              placeholder="Feature A, Feature B, Feature C"
              value={newFeaturesInput}
              onChange={e => setNewFeaturesInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') addFeatures(); }}
            />
          </Form.Group>
          <Button onClick={addFeatures} disabled={loading}>Add Features</Button>
        </Card.Body>
      </Card>

      {features.length > 0 ? (
        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th>Feature Name</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {features.map(f => (
              <tr key={f.id}>
                <td>{f.name}</td>
                <td>
                  <Button size="sm" variant="outline-danger" onClick={() => deleteFeature(f)} disabled={loading}>Delete</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : (
        <Card><Card.Body><p className="text-muted">No features defined yet.</p></Card.Body></Card>
      )}
    </>
  );

  const renderHistoryContent = () => (
    <>
      <Card className="mb-3">
        <Card.Body>
          <Row>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label><strong>Date</strong></Form.Label>
                <Form.Control
                  type="date"
                  value={historyFormData.date}
                  onChange={e => setHistoryFormData(prev => ({ ...prev, date: e.target.value }))}
                />
              </Form.Group>
            </Col>
          </Row>
          <Form.Group className="mb-3">
            <Form.Label><strong>Details</strong></Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={historyFormData.details}
              onChange={e => setHistoryFormData(prev => ({ ...prev, details: e.target.value }))}
            />
          </Form.Group>
          <div className="d-flex gap-2">
            <Button onClick={saveHistoryItem} disabled={loading}>
              {selectedHistoryItemId ? 'Update Item' : 'Add Item'}
            </Button>
            <Button variant="secondary" onClick={resetHistoryForm} disabled={loading}>New</Button>
          </div>
        </Card.Body>
      </Card>

      {historyItems.length === 0 ? (
        <Card>
          <Card.Body>
            <p className="text-muted">No history items yet.</p>
          </Card.Body>
        </Card>
      ) : (
        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th>Date</th>
              <th>Details</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {historyItems.map(item => (
              <tr key={item.id} className={selectedHistoryItemId === item.id ? 'table-active' : ''}>
                <td>{item.date}</td>
                <td className="notes-cell">{item.details}</td>
                <td>
                  <div className="d-flex gap-2">
                    <Button size="sm" variant="outline-primary" onClick={() => selectHistoryItemForEdit(item)}>Edit</Button>
                    <Button size="sm" variant="outline-danger" onClick={() => deleteHistoryItem(item)} disabled={loading}>Delete</Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </>
  );

  const renderHistorySection = () => (
    <div className="deployment-history-section mt-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">Deployment History{historyDeployment ? `: ${historyDeployment.name}` : ''}</h4>
        <Button variant="outline-secondary" size="sm" onClick={closeHistory}>
          Close
        </Button>
      </div>

      {historyItems.length === 0 ? (
        <Card>
          <Card.Body>
            <p className="text-muted">No history items yet.</p>
          </Card.Body>
        </Card>
      ) : (
        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th>Date</th>
              <th>Details</th>
            </tr>
          </thead>
          <tbody>
            {historyItems.map(item => (
              <tr key={item.id}>
                <td>{item.date}</td>
                <td className="notes-cell">{item.details}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </div>
  );

  const renderHistoryPage = () => (
    <>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">Edit History{historyDeployment ? `: ${historyDeployment.name}` : ''}</h4>
        <Button variant="outline-secondary" size="sm" onClick={closeHistoryPage}>
          Back
        </Button>
      </div>
      {renderHistoryContent()}
    </>
  );

  return (
    <div className="deployments-container">
      <h1>Deployments</h1>

      {statusMessage && (
        <Alert variant={statusType}>
          <strong>Status:</strong> {statusMessage}
        </Alert>
      )}

      <Row className="main-content-flex">
        {renderSidebar()}
        <div className="center-content">
          {activeView === 'view' && renderViewDeployments()}
          {activeView === 'modify' && renderModifyDeployment()}
          {activeView === 'manage' && renderManageFeatures()}
          {activeView === 'history' && renderHistoryPage()}
        </div>
      </Row>
    </div>
  );
};

export default Deployments;
